import { Typography } from '~/components/common/Typography'
import { SettingsUploadStep } from '~/components/modals/OnboardingDialog'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'

/**
 * Full content-area view shown when the current workspace has not uploaded its
 * own MuleSoft settings.xml and is still resolving builds against Curie's
 * bundled EE credentials.
 *
 * Rendered by `ProtectedRoute` in place of whatever route the user asked for —
 * including a URL typed directly — so no page underneath ever mounts or
 * fetches. Same shape as `AccountLockedView`: the sidebar stays alongside it,
 * so the user can switch to a workspace that already complies, or log out.
 *
 * - Workspace admins get the upload form inline and unlock by saving.
 * - Everyone else is told who can fix it; the upload endpoint requires admin
 *   access, so showing them the form would only dead-end.
 */
export function WorkspaceSettingsXmlLock() {
  const { currentWorkspace, refetch } = useWorkspaceContext()
  const isAdmin = currentWorkspace?.is_admin === true

  return (
    <div className='flex flex-col items-center justify-center h-full w-full p-8 bg-tokens-neutral-bg-primary'>
      <div className='flex flex-col gap-6 w-full max-w-[560px]'>
        <div className='flex flex-col gap-3'>
          <Typography
            fontFamily='geist'
            fontSize='headlineMd'
            className='text-tokens-neutral-text-primary'
          >
            Settings required
          </Typography>
          <Typography
            fontFamily='geist'
            fontSize='bodyMd'
            className='text-tokens-neutral-text-secondary'
          >
            {isAdmin
              ? 'This workspace needs your own Maven settings.xml before you can use it. Builds cannot run on CurieTech’s bundled MuleSoft credentials.'
              : 'Ask your workspace admin to upload settings.xml to continue. You can switch to another workspace from the sidebar in the meantime.'}
          </Typography>
        </div>

        {isAdmin && <SettingsUploadStep variant='lock' refreshWorkspace={refetch} />}
      </div>
    </div>
  )
}

export default WorkspaceSettingsXmlLock
